import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Button } from 'components/ui/button';
import { Alert, Form, Row, Col } from 'react-bootstrap-5';
import { isEmpty, pick } from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSparkles } from '@fortawesome/pro-solid-svg-icons';
import { connect } from 'react-redux';

import AutocompletedAddressFormGroup from 'components/form/autocompleted_address_form_group';
import BrokersList from './brokers_list';
import ErrorAlert from './error_alert';
import { countryListPropType } from './prop_types';


const MULTI_FAMILY_PROPERTY_TYPE_ID = 6;

const SUBMIT_ACTIONS = {
  SEARCH_OR_CREATE_LISTING: 'search_or_create_listing',
  CREATE_DOC: 'create_doc'
};

export function CreatePropertyForm({
  addressNotFound,
  brokers,
  countryList,
  errorMessages,
  formFields,
  gotoPrevStep,
  showCreateDocButton,
  hasErrors,
  hasInsights,
  listingNotFound,
  maxBrokersLimit,
  newListing,
  propertyTypes,
  saving,
  showBrokerSelect,
  onBrokerChange,
  onChange,
  onFormSubmit,
  onFormSubmitWithDocument,
}) {
  // ============================================================================
  // FORM SUBMISSION STATE
  // ============================================================================
  // We have two submit buttons with different behaviors:
  // 1. "Search/Create Listing" button - standard form submission
  // 2. "View Proposal" button - creates listing + document, redirects to editor
  //
  // - `showValidation`: true after ANY submit button is clicked (shows validation errors)
  // - `activeSubmitAction`: which button initiated the current save (SUBMIT_ACTIONS value | null)
  //
  // The `saving` prop from parent is true when API call is in progress (i.e. "pending").
  // We combine it with `activeSubmitAction` to show pending state on correct button.
  // ============================================================================
  const [showValidation, setShowValidation] = useState(false);
  const [activeSubmitAction, setActiveSubmitAction] = useState(null);

  /* eslint-disable camelcase */
  const {
    address,
    city,
    country,
    state,
    zip,
    sale_or_lease,
    property_type_id,
    generate_sale_description,
    generate_sale_bullets,
    generate_lease_description,
    generate_lease_bullets
  } = formFields;
  /* eslint-enable camelcase */

  const isStateRequired = country.countryStates.length !== 0;
  const isZipRequired = country.zipLabel !== '';

  const isAddressInvalid = !address;
  const isCityInvalid = !city;
  const isCountryInvalid = !country?.hasOwnProperty('id');
  const isStateInvalid = isStateRequired && !state;
  const isZipInvalid = isZipRequired && !zip;
  const isPropertyTypeInvalid = !property_type_id; // eslint-disable-line camelcase
  const isBrokersInvalid = showBrokerSelect && brokers.some(item => isEmpty(item));
  const isSaleOrLeaseInvalid = !sale_or_lease; // eslint-disable-line camelcase
  const isMultiFamily = Number(property_type_id) === MULTI_FAMILY_PROPERTY_TYPE_ID; // eslint-disable-line camelcase

  // Derived state for button pending/attempted status
  const isSearchOrCreatePending = saving && activeSubmitAction === SUBMIT_ACTIONS.SEARCH_OR_CREATE_LISTING;
  const isCreateDocPending = saving && activeSubmitAction === SUBMIT_ACTIONS.CREATE_DOC;
  const createDocAttempted = activeSubmitAction === SUBMIT_ACTIONS.CREATE_DOC;

  const isFormInvalid = isAddressInvalid ||
    isCityInvalid ||
    isCountryInvalid ||
    isStateInvalid ||
    isZipInvalid ||
    isPropertyTypeInvalid ||
    isBrokersInvalid;

  const handleSearchOrCreateClick = (e) => {
    e.preventDefault();
    setShowValidation(true);
    setActiveSubmitAction(SUBMIT_ACTIONS.SEARCH_OR_CREATE_LISTING);
    if (!isFormInvalid) onFormSubmit();
  };

  const handleCreateDocClick = (e) => {
    if (e) e.preventDefault();
    setShowValidation(true);
    setActiveSubmitAction(SUBMIT_ACTIONS.CREATE_DOC);
    if (!isFormInvalid && !isSaleOrLeaseInvalid) onFormSubmitWithDocument();
  };

  const handleFormSubmit = (e) => {
    handleSearchOrCreateClick(e);
  };

  const searchOrCreateButtonText = () => {
    if (listingNotFound) {
      return isSearchOrCreatePending ? 'Creating Listing...' : 'Create Listing';
    } else if (newListing) {
      return isSearchOrCreatePending ? 'Searching...' : 'Search';
    }
    return '';
  };

  const renderSubmitButtons = () => {
    const showBothButtons = showCreateDocButton;

    const searchButton = (
      <Button
        data-pendo-tag="prop_form_create_listing_button"
        disabled={saving || isFormInvalid}
        name="submit-listing"
        type="submit"
        variant="primary"
      >
        {searchOrCreateButtonText()}
      </Button>
    );

    const createDocButton = showBothButtons && (
      <Button
        data-pendo-tag="prop_form_create_proposal_button"
        disabled={saving || isFormInvalid || isSaleOrLeaseInvalid}
        name="submit-listing-with-document"
        type="button"
        variant="outline"
        onClick={handleCreateDocClick}
      >
        {isCreateDocPending ? 'Creating Proposal...' : 'View Proposal'}
      </Button>
    );

    const backButton = gotoPrevStep && (
      <Button variant="ghost" onClick={gotoPrevStep}>Back</Button>
    );

    // Layout: [secondary] ... [Back] [primary]
    // Secondary on far left, Back immediately left of primary, primary on far right
    if (showBothButtons) {
      return (
        <div className="mt-2 d-flex justify-content-between">
          <div>
            {createDocButton}
          </div>
          <div className="d-flex">
            {backButton && <span className="me-3">{backButton}</span>}
            {searchButton}
          </div>
        </div>
      );
    }

    // Only one button (search/create) - original layout
    return (
      <div className="mt-2 d-flex justify-content-end">
        {backButton && <span className="me-3">{backButton}</span>}
        {searchButton}
      </div>
    );
  };

  const renderSaleOrLeaseFields = () => {
    return (
      <>
        {/* NOTE: this is a special field that's only required when creating a document - see `createDocAttempted` */}
        {showCreateDocButton && (
          <Form.Group className={`form-group ${createDocAttempted ? 'required' : ''}`}>
            <Form.Label>Sale or Lease</Form.Label>
            {/* eslint-disable camelcase */}
            <Form.Select
              className="form-control"
              isInvalid={isSaleOrLeaseInvalid && createDocAttempted && showValidation}
              name="sale_or_lease"
              value={sale_or_lease || ''}
              onChange={e => onChange({ sale_or_lease: e.target.value })}
            >
              <option value="">Select...</option>
              <option value="sale">Sale</option>
              {!isMultiFamily && <option value="lease">Lease</option>}
            </Form.Select>
            {isSaleOrLeaseInvalid && createDocAttempted && showValidation && (
              <Form.Control.Feedback type="invalid">
                Sale or Lease is required to create a document
              </Form.Control.Feedback>
            )}
            {/* eslint-enable camelcase */}
          </Form.Group>
        )}


        {/* eslint-disable-next-line camelcase */}
        {showCreateDocButton && sale_or_lease && (
          <Form.Group className="form-group">
            <Form.Label className="d-flex align-items-center gap-2">
              <FontAwesomeIcon icon={faSparkles} style={{ color: '#924FDC' }} />
              AI Content Generation
            </Form.Label>
            {/* eslint-disable camelcase */}
            {sale_or_lease === 'sale' && (
              <>
                <Form.Check
                  checked={generate_sale_description === true}
                  id="generate-sale-description"
                  label="Auto-generate sale description"
                  type="switch"
                  onChange={e => onChange({ generate_sale_description: e.target.checked })}
                />
                <Form.Check
                  checked={generate_sale_bullets === true}
                  id="generate-sale-bullets"
                  label="Auto-generate sale highlights"
                  type="switch"
                  onChange={e => onChange({ generate_sale_bullets: e.target.checked })}
                />
              </>
            )}
            {sale_or_lease === 'lease' && (
              <>
                <Form.Check
                  checked={generate_lease_description === true}
                  id="generate-lease-description"
                  label="Auto-generate lease description"
                  type="switch"
                  onChange={e => onChange({ generate_lease_description: e.target.checked })}
                />
                <Form.Check
                  checked={generate_lease_bullets === true}
                  id="generate-lease-bullets"
                  label="Auto-generate lease highlights"
                  type="switch"
                  onChange={e => onChange({ generate_lease_bullets: e.target.checked })}
                />
              </>
            )}
            {/* eslint-enable camelcase */}
          </Form.Group>
        )}
      </>
    );
  };

  return (
    <>
      <h5 className="mb-5 mt-0 text-center">Create a new listing</h5>

      {newListing && (
        <>
          <div className="mb-5 text-center">
            Search for a property and Buildout Insights will auto-populate the property information we have,
            or you can create a brand new listing.
          </div>

          <h6 className="my-4">Search for Property</h6>
        </>
      )}
      <ErrorAlert errorMessages={errorMessages} hasErrors={hasErrors}/>
      {listingNotFound && hasInsights && !hasErrors && (
        <>
          <Alert className="d-flex mb-5" variant="info">
            <p className="fs-large m-0">
              The address {addressNotFound} was not found in Insights. Creating new property.
            </p>
          </Alert>
        </>
      )}

      <Form autoComplete="off" className="form-vertical-field-container" noValidate onSubmit={handleFormSubmit}>
        {countryList.length > 1 && (
          <Form.Group className="form-group required">
            <Form.Label>Country</Form.Label>
            <Form.Select
              autoFocus={countryList.length > 1}
              className="form-control"
              isInvalid={isCountryInvalid && showValidation}
              name="country"
              required
              value={country.id}
              onChange={(e) => {
                const selectedCountry = countryList.find(country => country.id === Number(e.target.value));
                onChange({ country: selectedCountry });
              }}
            >
              {countryList.map(country => (
                <option key={country.id} value={country.id}>{country.name}</option>
              ))}
            </Form.Select>
            {isCountryInvalid && showValidation && <Form.Control.Feedback type="invalid">
              This field is required
            </Form.Control.Feedback>}
          </Form.Group>
        )}

        <AutocompletedAddressFormGroup
          {...pick(formFields, ['country', 'address', 'city', 'state', 'zip'])}
          feedback={isAddressInvalid && showValidation && 'This field is required'}
          feedbackType="invalid"
          hasError={isAddressInvalid && showValidation}
          label={<Form.Label>Address</Form.Label>}
          required
          onChange={onChange}
        />

        <Form.Group className="form-group required">
          <Form.Label>City</Form.Label>
          <Form.Control
            isInvalid={isCityInvalid && showValidation}
            name="city"
            required
            type="text"
            value={city}
            onChange={e => onChange({ city: e.target.value })}
          />
          {isCityInvalid && showValidation && <Form.Control.Feedback type="invalid">
            This field is required
          </Form.Control.Feedback>}
        </Form.Group>

        <Form.Group>
          <Row>
            {(country.countryStates.length !== 0) && (
              <Col md={8}>
                <div className="form-group required">
                  <Form.Label>{country.stateLabel}</Form.Label>
                  <Form.Select
                    className="form-control"
                    isInvalid={isStateInvalid && showValidation}
                    name="state"
                    required
                    value={state}
                    onChange={e => onChange({ state: e.target.value })}
                  >
                    <option value="">Select a state</option>
                    {country.countryStates.map(state => (
                      <option key={state.code} value={state.code}>{state.displayName}</option>
                    ))}
                  </Form.Select>
                  {isStateInvalid && showValidation && <Form.Control.Feedback type="invalid">
                    This field is required
                  </Form.Control.Feedback>}
                </div>
              </Col>
            )}

            {country.zipLabel && (
              <Col>
                <div className="form-group required">
                  <Form.Label>{country.zipLabel}</Form.Label>
                  <Form.Control
                    color="red"
                    isInvalid={isZipInvalid && showValidation}
                    name="zip"
                    required
                    type="string"
                    value={zip}
                    onChange={e => onChange({ zip: e.target.value })}
                  />
                  {isZipInvalid && showValidation && <Form.Control.Feedback type="invalid">
                    This field is required
                  </Form.Control.Feedback>}
                </div>
              </Col>
            )}
          </Row>
        </Form.Group>

        {showBrokerSelect && <Form.Group className="form-group">
          <BrokersList
            brokers={brokers}
            isInvalid={isBrokersInvalid && showValidation}
            maxBrokersLimit={maxBrokersLimit}
            required={true}
            onSetBrokers={brokers => onBrokerChange(brokers)}
          />
          {isBrokersInvalid && showValidation && (
            <div className="text-danger small mt-1">
              This field is required
            </div>
          )}
        </Form.Group>}

        <Form.Group className="form-group required">
          <Form.Label>Primary Property Type</Form.Label>
          {/* eslint-disable camelcase */}
          <Form.Select
            className="form-control"
            isInvalid={isPropertyTypeInvalid && showValidation}
            name="property_type_id"
            required
            value={property_type_id || ''}
            onChange={e => onChange({
              property_type_id: e.target.value,
              // Lease isn't offered for multifamily, so drop a previously selected lease
              ...(Number(e.target.value) === MULTI_FAMILY_PROPERTY_TYPE_ID && sale_or_lease === 'lease'
                ? { sale_or_lease: '' } : {})
            })}
          >
            <option value="">Select a property type</option>
            {propertyTypes && Object.entries(propertyTypes).map(([key, value]) => (
              <option key={key} value={key}>{value}</option>
            ))}
          </Form.Select>
          {/* eslint-enable camelcase */}
          {isPropertyTypeInvalid && showValidation && <Form.Control.Feedback type="invalid">
            This field is required
          </Form.Control.Feedback>}
        </Form.Group>

        {renderSaleOrLeaseFields()}

        {renderSubmitButtons()}
      </Form>
    </>
  );
}

CreatePropertyForm.propTypes = {
  addressNotFound: PropTypes.string.isRequired,
  brokers: PropTypes.array.isRequired,
  countryList: countryListPropType.isRequired,
  errorMessages: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.array
  ]),
  formFields: PropTypes.object.isRequired,
  gotoPrevStep: PropTypes.func,
  hasErrors: PropTypes.bool.isRequired,
  hasInsights: PropTypes.bool.isRequired,
  listingNotFound: PropTypes.bool.isRequired,
  maxBrokersLimit: PropTypes.number.isRequired,
  newListing: PropTypes.bool.isRequired,
  propertyTypes: PropTypes.objectOf(PropTypes.string),
  saving: PropTypes.bool.isRequired,
  showBrokerSelect: PropTypes.bool.isRequired,
  showCreateDocButton: PropTypes.bool,
  onBrokerChange: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  onFormSubmit: PropTypes.func.isRequired,
  onFormSubmitWithDocument: PropTypes.func,
};

const mapStateToProps = state => ({
  propertyTypes: state.propertyTypes
});

export default connect(mapStateToProps)(CreatePropertyForm);
